import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {NotificationChannel, toNotificationChannel} from "./notification-channel";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {NotificationReceiverType, toNotificationReceiverType} from "./notification-receiver-type";

export const NotificationReceiverProfileCodec = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    channel: t.string,
    receiverType: t.string,
    emailThreadingEnabled: t.boolean,
    doNotUse: t.boolean,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    inboundAddressPattern: t.string,
    sesRecipientDomain: t.string,
    sesAwsRegion: t.string,
    sesSqsQueueName: t.string,
    sesSqsDlqQueueName: t.string,
    createdById: t.string,
    lastModifiedById: t.string,
  }),
]);

export type NotificationReceiverProfile = t.TypeOf<typeof NotificationReceiverProfileCodec>;

@Exclude()
export class NotificationReceiverProfileEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  name!: string;

  @Transform(getDatabaseCodeTransformer(toNotificationChannel))
  @Expose()
  channel!: NotificationChannel;

  @Transform(getDatabaseCodeTransformer(toNotificationReceiverType))
  @Expose()
  receiverType!: NotificationReceiverType;

  @Expose()
  inboundAddressPattern?: string;

  @Expose()
  emailThreadingEnabled!: boolean;

  @Expose()
  sesRecipientDomain?: string;

  @Expose()
  sesAwsRegion?: string;

  @Expose()
  sesSqsQueueName?: string;

  @Expose()
  sesSqsDlqQueueName?: string;

  @Expose()
  doNotUse!: boolean;

  @Expose()
  createDttm!: number;

  @Expose()
  lastModifiedDttm!: number;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  readonly entityType = 'NOTIFRECVPROFILE';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for NotificationReceiverProfileEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: NotificationReceiverProfile): NotificationReceiverProfileEntity {
    return plainToInstance(NotificationReceiverProfileEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<NotificationReceiverProfile> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(NotificationReceiverProfileCodec, object);
  }

  asTypeValue(): NotificationReceiverProfile {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(NotificationReceiverProfileCodec, object);
  }
}
